import React, { FC, useEffect, useState, useRef, createContext, useContext } from "react";

import { Box } from "../Box/Box";
import { Flex } from "../Flex/Flex";
import { Text } from "../Text/Text";
import useMediaQuery from "../../hooks/useMediaQuery";
import useIsVisible from "../../hooks/useIsVisible";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

import { FA_ICONS, STROKE, ARC_LEN, ARC_PATH, getMilestones, getPos } from "./constants";
import { playMilestoneSound, playFanfareSound } from "./audio";
import { useTween, resolveCopy } from "./hooks";
import type { Milestone, SvgIconProps, CopyBlockProps, RewardIconProps, ArcDotProps, IGaugeProps } from "./types";

export type { IGaugeProps, IconSetType } from "./types";
export { ICON_SETS } from "./constants";

// ── Contexts ──────────────────────────────────────────────────────────────────
const GaugeTweenCtx = createContext<number>(0);
const GaugeVariantCtx = createContext<{ isFlat: boolean }>({ isFlat: false });

// ── Internal Components ───────────────────────────────────────────────────────

const SvgIcon: FC<SvgIconProps> = ({ name, size = 16, color = "currentColor" }) => {
  const icon = FA_ICONS[name];
  if (!icon) return null;
  return (
    <FontAwesomeIcon
      icon={icon}
      style={{ width: size, height: size, color }}
    />
  );
};

const ScoreRoller: FC<{ score: number }> = ({ score }) => {
  const v = Math.max(0, Math.min(8, score));
  return (
    <Box display="inline-block" overflow="hidden" h="1em" verticalAlign="baseline" className="rt-roller">
      <Box
        display="flex"
        flexDirection="column"
        transition="transform 0.6s cubic-bezier(0.22,1,0.36,1)"
        transform={`translateY(-${v}em)`}
        className="rt-roller-strip"
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
          <Box key={n} h="1em" lineHeight="1" display="block" className="rt-roller-digit">
            {n}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ScoreFraction: FC<{ lockedTarget: number | null }> = ({ lockedTarget }) => {
  const tweenScore = useContext(GaugeTweenCtx);
  const display = lockedTarget !== null ? lockedTarget : tweenScore;
  return (
    <Flex align="baseline" gap="1px" lineHeight="1" fontSize="5xl" fontWeight="700" fontFamily="heading" color="gray.800" className="rt-score-number">
      <ScoreRoller score={display} />
      <Box>/8</Box>
    </Flex>
  );
};

const AvatarBlock: FC<{ src?: string }> = ({ src }) => {
  return (
    <Box position="relative" display="inline-block" className="rt-avatar-wrap">
      <Box
        w="56px"
        h="56px"
        borderRadius="50%"
        bgSize="cover"
        bgPosition="center"
        bg="#EBEBEB"
        border="2px solid #FF478F"
        backgroundImage={src ? `url(${src})` : "none"}
        className="rt-avatar-img"
      />
      <Box
        position="absolute"
        bottom="-2px"
        right="-4px"
        w="22px"
        h="22px"
        borderRadius="50%"
        bg="linear-gradient(135deg,#FF478F,#FF9800)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="2px solid #fff"
        className="rt-avatar-badge"
      >
        <SvgIcon name="medal" size={12} color="#fff" />
      </Box>
    </Box>
  );
};

const CopyBlock: FC<CopyBlockProps> = ({ score, hoveredReward, avatarSrc, isFlat = false }) => {
  const [entry, lockedTarget] = resolveCopy(score, hoveredReward);
  const isHoverState = hoveredReward !== null;

  return (
    <Flex
      textAlign="center"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="3px"
      w={isFlat ? "200px" : "220px"}
      h={isFlat ? "130px" : undefined}
      className="rt-copy-block"
    >
      {isFlat && !isHoverState ? (
        <Text
          fontSize="md"
          fontWeight="bold"
          fontFamily="body"
          color="black"
          textAlign="center"
          lineHeight="1.4"
          className="rt-center-pre"
        >
          <FormattedMessage {...messages.yourScore} />
        </Text>
      ) : (
        entry.pre && (
          <Text
            fontSize="md"
            fontWeight="bold"
            fontFamily="body"
            color="black"
            textAlign="center"
            lineHeight="1.4"
            className="rt-center-pre"
          >
            {entry.pre}
          </Text>
        )
      )}

      {entry.avatar ? (
        <AvatarBlock src={avatarSrc} />
      ) : (
        <ScoreFraction lockedTarget={isHoverState ? lockedTarget : null} />
      )}

      {entry.heading && (
        <Text
          fontSize={score >= 8 ? "md" : "sm"}
          fontWeight="600"
          color="gray.800"
          textAlign="center"
          lineHeight="1.35"
          className="rt-center-heading"
        >
          {entry.heading}
        </Text>
      )}

      {entry.sub && (
        <Text
          fontSize="sm"
          color="gray.500"
          textAlign="center"
          lineHeight="1.5"
          className="rt-center-sub"
        >
          {entry.sub}
        </Text>
      )}
    </Flex>
  );
};

const CenterBlock: FC<{ score: number; hoveredReward: number | null; avatarSrc?: string }> = ({ score, hoveredReward, avatarSrc }) => {
  return (
    <Box
      position="absolute"
      top="72%"
      left="50%"
      transform="translate(-50%, -50%)"
      pointerEvents="none"
      className="rt-center"
    >
      <CopyBlock
        score={score}
        hoveredReward={hoveredReward}
        avatarSrc={avatarSrc}
        isFlat={false}
      />
    </Box>
  );
};

const RewardIcon: FC<RewardIconProps> = ({
  milestone,
  tweenScore,
  hovered,
  hoverable,
  isMobile,
  onHover,
}) => {
  const { isFlat } = useContext(GaugeVariantCtx);
  const p = getPos(milestone.at, isFlat);
  const unlocked = tweenScore >= milestone.at;
  const filled = (hovered && hoverable) || unlocked;
  const size = isFlat ? 48 : 32;
  const iconSize = isFlat ? (milestone.isBadge ? 22 : 20) : (milestone.isBadge ? 15 : 13);

  return (
    <g
      transform={`translate(${p.x} ${p.y})`}
      style={{ cursor: hoverable ? "pointer" : "default" }}
    >
      {hoverable && (
        <circle
          r={size / 2 + 14}
          fill="transparent"
          onMouseEnter={!isMobile ? () => onHover(true) : undefined}
          onMouseLeave={!isMobile ? () => onHover(false) : undefined}
          onClick={isMobile ? () => onHover(!hovered) : undefined}
          style={{ pointerEvents: "all", WebkitTapHighlightColor: "transparent" }}
        />
      )}
      <foreignObject
        x={-size / 2}
        y={-size / 2}
        width={size}
        height={size}
        style={{ pointerEvents: "none" }}
      >
        <div
          className={`rt-reward-chip${filled ? " rt-reward-chip-filled" : ""}`}
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: filled ? "linear-gradient(to right, #FF478F, #FF9800)" : "#EBEBEB",
            border: filled ? "2px solid #fff" : "2px solid #666666",
            transition: "background 0.35s, border-color 0.35s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
          }}
        >
          <SvgIcon
            name={milestone.icon}
            size={iconSize}
            color={filled ? "#fff" : "#666666"}
          />
        </div>
      </foreignObject>
    </g>
  );
};

const ArcDot: FC<ArcDotProps> = ({ at, tweenScore }) => {
  const { isFlat } = useContext(GaugeVariantCtx);
  const p = getPos(at, isFlat);
  return (
    <circle
      cx={p.x}
      cy={p.y}
      r={3.2}
      fill={tweenScore >= at - 0.05 ? "#fff" : "#CCCCCC"}
      style={{ transition: "fill .35s ease" }}
    />
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export const Gauge: FC<IGaugeProps> = ({
  score: targetScore,
  replayToken = 0,
  soundOn = true,
  iconSet = "Original",
  avatarSrc,
  variant = "circular",
  ...rest
}) => {
  const clamped = Math.max(0, Math.min(8, targetScore));
  const MILESTONES = getMilestones(iconSet);
  const [hoveredReward, setHoveredReward] = useState<number | null>(null);
  const { isMobile } = useMediaQuery();
  const firstRef = useRef(true);
  const hoverResetTimerRef = useRef<number | null>(null);
  const { ref: containerRef, isVisible } = useIsVisible<HTMLDivElement>();

  const handleHoverChange = (val: boolean, reward: number) => {
    if (hoverResetTimerRef.current !== null) {
      clearTimeout(hoverResetTimerRef.current);
      hoverResetTimerRef.current = null;
    }
    setHoveredReward(val ? reward : null);
    if (val && isMobile) {
      hoverResetTimerRef.current = window.setTimeout(() => setHoveredReward(null), 3000);
    }
  };

  useEffect(() => () => {
    if (hoverResetTimerRef.current !== null) clearTimeout(hoverResetTimerRef.current);
  }, []);

  const tweenScore = useTween(isVisible ? clamped : 0, {
    ms: 1500,
    startFrom: firstRef.current || replayToken > 0 ? 0 : undefined,
    replayToken,
  });

  useEffect(() => {
    firstRef.current = false;
  }, []);

  const prevRef = useRef(0);
  useEffect(() => {
    const cur = Math.floor(tweenScore);
    if (cur > prevRef.current) {
      for (let i = prevRef.current + 1; i <= cur; i++) {
        if (!soundOn) continue;
        if (i === 8) {
          playFanfareSound();
        } else if (i === 6 || i === 7) {
          playMilestoneSound();
        }
      }
    }
    prevRef.current = cur;
  }, [tweenScore, soundOn]);

  const filled = (tweenScore / 8) * ARC_LEN;
  const useBrand = clamped >= 6;
  const isHoverable = (m: Milestone) => clamped < m.at;
  const hoveredMilestone = hoveredReward !== null
    ? MILESTONES.find((m) => m.reward === hoveredReward)
    : undefined;
  const activeHover = hoveredMilestone && isHoverable(hoveredMilestone) ? hoveredReward : null;

  if (variant === "flat") {
    return (
      <GaugeVariantCtx.Provider value={{ isFlat: true }}>
        <GaugeTweenCtx.Provider value={tweenScore}>
          <Flex
            ref={containerRef}
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="space-between"
            w={{ base: "100%", md: "720px" }}
            bg="transparent"
            overflow="visible"
            aria-label={`Progress ${Math.round(targetScore)} of 8`}
            className="rt-gauge rt-gauge-flat"
            gap={{ base: 6, md: 8 }}
            {...rest}
          >
            <CopyBlock
              score={clamped}
              hoveredReward={activeHover}
              avatarSrc={avatarSrc}
              isFlat={true}
            />

            <Box w={{ base: "100%", md: "500px" }} h="80px" position="relative">
              <svg
                viewBox="0 0 500 80"
                style={{ width: "100%", height: "100%", overflow: "visible" }}
              >
                <defs>
                  <linearGradient
                    id="rt-arc-brand-flat"
                    gradientUnits="userSpaceOnUse"
                    x1="20"
                    y1="40"
                    x2="480"
                    y2="40"
                  >
                    <stop offset="0%" stopColor="#FF478F" />
                    <stop offset="100%" stopColor="#FF9800" />
                  </linearGradient>
                  <linearGradient
                    id="rt-arc-green-flat"
                    gradientUnits="userSpaceOnUse"
                    x1="20"
                    y1="40"
                    x2="480"
                    y2="40"
                  >
                    <stop offset="0%" stopColor="#74C7AC" />
                    <stop offset="100%" stopColor="#18A175" />
                  </linearGradient>
                </defs>

                <line
                  x1={20}
                  y1={40}
                  x2={480}
                  y2={40}
                  stroke="#EBEBEB"
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                />

                <line
                  x1={20}
                  y1={40}
                  x2={20 + (tweenScore / 8) * 460}
                  y2={40}
                  stroke="url(#rt-arc-brand-flat)"
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                  style={{
                    opacity: tweenScore > 0 ? (useBrand ? 1 : 0) : 0,
                    transition: "opacity .8s ease",
                  }}
                />

                <line
                  x1={20}
                  y1={40}
                  x2={20 + (tweenScore / 8) * 460}
                  y2={40}
                  stroke="url(#rt-arc-green-flat)"
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                  style={{
                    opacity: tweenScore > 0 ? (useBrand ? 0 : 1) : 0,
                    transition: "opacity .8s ease",
                  }}
                />

                {[1, 2, 3, 4, 5, 6, 7].map((at) => (
                  <ArcDot key={at} at={at} tweenScore={tweenScore} />
                ))}

                {MILESTONES.map((m) => (
                  <RewardIcon
                    key={m.at}
                    milestone={m}
                    tweenScore={tweenScore}
                    hovered={hoveredReward === m.reward}
                    hoverable={isHoverable(m)}
                    isMobile={isMobile}
                    onHover={(val) => handleHoverChange(val, m.reward)}
                  />
                ))}
              </svg>
            </Box>
          </Flex>
        </GaugeTweenCtx.Provider>
      </GaugeVariantCtx.Provider>
    );
  }

  return (
    <GaugeVariantCtx.Provider value={{ isFlat: false }}>
      <GaugeTweenCtx.Provider value={tweenScore}>
        <Box
          ref={containerRef}
          position="relative"
          w="360px"
          h="193px"
          bg="transparent"
          overflow="visible"
          aria-label={`Progress ${Math.round(targetScore)} of 8`}
          className="rt-gauge"
          {...rest}
        >
          <svg
            viewBox="0 0 360 193"
            style={{ width: "100%", height: "100%", overflow: "visible" }}
          >
            <defs>
              <linearGradient id="rt-arc-brand-circular" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#FF478F" />
                <stop offset="100%" stopColor="#FF9800" />
              </linearGradient>
              <linearGradient id="rt-arc-green-circular" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#74C7AC" />
                <stop offset="100%" stopColor="#18A175" />
              </linearGradient>
            </defs>

            <path
              d={ARC_PATH}
              fill="none"
              stroke="#EBEBEB"
              strokeWidth={STROKE}
              strokeLinecap="round"
            />

            <path
              d={ARC_PATH}
              fill="none"
              stroke="url(#rt-arc-brand-circular)"
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={`${filled} ${ARC_LEN}`}
              style={{
                opacity: useBrand ? 1 : 0,
                transition: "opacity .8s ease",
              }}
            />

            <path
              d={ARC_PATH}
              fill="none"
              stroke="url(#rt-arc-green-circular)"
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={`${filled} ${ARC_LEN}`}
              style={{
                opacity: useBrand ? 0 : 1,
                transition: "opacity .8s ease",
              }}
            />

            {[1, 2, 3, 4, 5, 6, 7].map((at) => (
              <ArcDot key={at} at={at} tweenScore={tweenScore} />
            ))}

            {MILESTONES.map((m) => (
              <RewardIcon
                key={m.at}
                milestone={m}
                tweenScore={tweenScore}
                hovered={hoveredReward === m.reward}
                hoverable={isHoverable(m)}
                isMobile={isMobile}
                onHover={(val) => handleHoverChange(val, m.reward)}
              />
            ))}
          </svg>

          <CenterBlock
            score={clamped}
            hoveredReward={activeHover}
            avatarSrc={avatarSrc}
          />
        </Box>
      </GaugeTweenCtx.Provider>
    </GaugeVariantCtx.Provider>
  );
};
